import React, { useState } from 'react';
import { Queue } from '../types';
import { X, Shield, AlertCircle, Users, Plus, Save, CheckCircle2, ChevronDown, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface AdminDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  queues: Queue[];
}
export function AdminDrawer({
  isOpen,
  onClose,
  queues
}: AdminDrawerProps) {
  const [selectedQueueId, setSelectedQueueId] = useState(queues[0]?.id);
  const [activeTab, setActiveTab] = useState<'groups' | 'roles' | 'profiles' | 'permSets'>('roles');
  const [mainTab, setMainTab] = useState<'rules' | 'analytics'>('rules');
  const [isSaved, setIsSaved] = useState(false);
  const queue = queues.find(q => q.id === selectedQueueId);
  const handleSave = () => {
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };
  if (!isOpen || !queue) return null;
  return <AnimatePresence data-id="element-11">
      {isOpen && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-12">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={onClose} data-id="element-13" />
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="relative w-[720px] bg-white h-full shadow-2xl flex flex-col" data-id="element-14">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50" data-id="element-15">
              <div className="flex items-center gap-3" data-id="element-16">
                <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center text-slate-600" data-id="element-17">
                  <Shield size={20} data-id="element-18" />
                </div>
                <div data-id="element-19">
                  <h2 className="text-lg font-semibold text-slate-900" data-id="element-20">
                    Admin Settings
                  </h2>
                  <p className="text-sm text-slate-500" data-id="element-21">
                    Manage visibility rules and queue analytics
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-200 rounded-full transition-colors" data-id="element-22">
                <X size={20} data-id="element-23" />
              </button>
            </div>

            {/* Main Tabs */}
            <div className="flex border-b border-slate-200 px-6 bg-white" data-id="element-24">
              <button onClick={() => setMainTab('rules')} className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${mainTab === 'rules' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-25">
                Queue Visibility Rules
              </button>
              <button onClick={() => setMainTab('analytics')} className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${mainTab === 'analytics' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-26">
                <BarChart3 size={16} data-id="element-27" />
                Queue Analytics
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50/30" data-id="element-28">
              {/* Queue Selector (Shared) */}
              <div data-id="element-29">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-30">
                  Select Queue to Manage
                </label>
                <div className="relative" data-id="element-31">
                  <select value={selectedQueueId} onChange={e => setSelectedQueueId(e.target.value)} className="w-full appearance-none bg-white border border-slate-300 rounded-lg py-2.5 pl-4 pr-10 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm" data-id="element-32">
                    {queues.map(q => <option key={q.id} value={q.id} data-id="element-33">
                        {q.name}
                      </option>)}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-34" />
                </div>
              </div>

              {mainTab === 'rules' ? <>
                  {/* Warnings */}
                  {queue.warnings && queue.warnings.length > 0 && <div className="space-y-2" data-id="element-35">
                      {queue.warnings.map((w, i) => <div key={i} className={`p-3 rounded-lg flex items-start gap-2 border ${w.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : w.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`} data-id="element-36">
                          <AlertCircle size={16} className="shrink-0 mt-0.5" data-id="element-37" />
                          <span className="text-sm font-medium" data-id="element-38">
                            {w.message}
                          </span>
                        </div>)}
                    </div>}

                  {/* Record Criteria (Read Only) */}
                  <div data-id="element-39">
                    <div className="flex items-center justify-between mb-3" data-id="element-40">
                      <h3 className="text-sm font-semibold text-slate-900" data-id="element-41">
                        Record Criteria
                      </h3>
                      <a href="#" className="text-xs font-medium text-blue-600 hover:text-blue-800" data-id="element-42">
                        Edit in Setup
                      </a>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 font-mono text-sm text-slate-700 space-y-2 shadow-sm" data-id="element-43">
                      {queue.recordCriteria.map((crit, i) => <div key={i} className="flex items-center gap-2" data-id="element-44">
                          {i > 0 && <span className="text-blue-500 font-bold text-xs" data-id="element-45">
                              AND
                            </span>}
                          <span className="bg-slate-50 px-2 py-1 rounded border border-slate-200" data-id="element-46">
                            {crit}
                          </span>
                        </div>)}
                    </div>
                  </div>

                  {/* Audience Criteria (Editable) */}
                  <div data-id="element-47">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-48">
                      Audience Criteria
                    </h3>
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-49">
                      <div className="flex border-b border-slate-200 bg-slate-50" data-id="element-50">
                        {(['groups', 'roles', 'profiles', 'permSets'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${activeTab === tab ? 'border-blue-600 text-blue-700 bg-white' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-51">
                            {tab.charAt(0).toUpperCase() + tab.slice(1).replace(/([A-Z])/g, ' $1').trim()}
                            <span className="ml-2 inline-flex items-center justify-center bg-slate-200 text-slate-600 text-[10px] rounded-full px-1.5 py-0.5" data-id="element-52">
                              {queue.audienceCriteria[tab].length}
                            </span>
                          </button>)}
                      </div>
                      <div className="p-4 min-h-[160px]" data-id="element-53">
                        <div className="flex flex-wrap gap-2 mb-4" data-id="element-54">
                          {queue.audienceCriteria[activeTab].map((item, i) => <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-100 text-blue-800 text-sm rounded-full" data-id="element-55">
                              {item}
                              <button className="text-blue-400 hover:text-blue-600 focus:outline-none" data-id="element-56">
                                <X size={14} data-id="element-57" />
                              </button>
                            </div>)}
                          {queue.audienceCriteria[activeTab].length === 0 && <p className="text-sm text-slate-400 italic py-1.5" data-id="element-58">
                              No {activeTab} assigned.
                            </p>}
                        </div>
                        <button className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors border border-dashed border-slate-300 hover:border-blue-400 rounded-full px-4 py-1.5" data-id="element-59">
                          <Plus size={16} data-id="element-60" />
                          Add {activeTab.replace(/([A-Z])/g, ' $1').trim()}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Effective Access Preview */}
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white shadow-lg" data-id="element-61">
                    <div className="flex items-center justify-between mb-4" data-id="element-62">
                      <div className="flex items-center gap-2" data-id="element-63">
                        <Users size={18} className="text-blue-400" data-id="element-64" />
                        <h3 className="text-sm font-semibold" data-id="element-65">
                          Effective Access Preview
                        </h3>
                      </div>
                      <a href="#" className="text-xs text-blue-300 hover:text-blue-200" data-id="element-66">
                        View all
                      </a>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-67">
                      <div data-id="element-68">
                        <div className="text-2xl font-bold" data-id="element-69">~47 users</div>
                        <div className="text-xs text-slate-400 mt-1" data-id="element-70">
                          Estimated impact based on current rules
                        </div>
                      </div>
                      <div className="flex -space-x-2" data-id="element-71">
                        {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-slate-600 border-2 border-slate-800 flex items-center justify-center text-[10px] font-bold" data-id="element-72">
                            U{i}
                          </div>)}
                        <div className="w-8 h-8 rounded-full bg-slate-700 border-2 border-slate-800 flex items-center justify-center text-[10px] font-bold" data-id="element-73">
                          +43
                        </div>
                      </div>
                    </div>
                  </div>
                </> /* Analytics Tab */ : <div className="space-y-6" data-id="element-74">
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-75">
                    <h3 className="text-sm font-semibold text-slate-900 mb-6" data-id="element-76">
                      Aging Distribution
                    </h3>
                    <div className="flex items-end gap-4 h-40" data-id="element-77">
                      {[40, 70, 100, 30].map((h, i) => <div key={i} className="flex-1 flex flex-col items-center gap-3" data-id="element-78">
                          <div className={`w-full rounded-t-md transition-all ${i === 2 ? 'bg-red-400' : i === 1 ? 'bg-orange-400' : 'bg-amber-400'}`} style={{
                    height: `${h}%`
                  }} data-id="element-79" />
                          <span className="text-xs font-medium text-slate-500" data-id="element-80">
                            {['<10', '10-14', '15-20', '21+'][i]}d
                          </span>
                        </div>)}
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-81">
                    <h3 className="text-sm font-semibold text-slate-900 mb-6" data-id="element-82">
                      Top Claimers (This Week)
                    </h3>
                    <div className="space-y-4" data-id="element-83">
                      {[{
                  name: 'Sarah Jenkins',
                  count: 14,
                  color: 'bg-blue-500'
                }, {
                  name: 'Mike Ross',
                  count: 9,
                  color: 'bg-indigo-500'
                }, {
                  name: 'Harvey Specter',
                  count: 5,
                  color: 'bg-purple-500'
                }].map((user, i) => <div key={i} className="flex items-center justify-between" data-id="element-84">
                          <div className="flex items-center gap-3" data-id="element-85">
                            <div className={`w-8 h-8 rounded-full ${user.color} text-white flex items-center justify-center text-xs font-bold`} data-id="element-86">
                              {user.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <span className="text-sm font-medium text-slate-700" data-id="element-87">
                              {user.name}
                            </span>
                          </div>
                          <span className="text-sm font-bold text-slate-900 bg-slate-100 px-3 py-1 rounded-full" data-id="element-88">
                            {user.count} claims
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>}
            </div>

            {/* Footer (Only show save for Rules tab) */}
            {mainTab === 'rules' && <div className="px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-between" data-id="element-89">
                <div className="text-xs text-slate-500" data-id="element-90">
                  Last updated by{' '}
                  <span className="font-medium text-slate-700" data-id="element-91">
                    System Admin
                  </span>{' '}
                  on Oct 24, 2023
                </div>
                <div className="flex gap-3" data-id="element-92">
                  <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-93">
                    Cancel
                  </button>
                  <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 shadow-sm" data-id="element-94">
                    {isSaved ? <CheckCircle2 size={16} data-id="element-95" /> : <Save size={16} data-id="element-96" />}
                    {isSaved ? 'Saved' : 'Save Changes'}
                  </button>
                </div>
              </div>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}